import React from 'react'
import {
  components,
  default as ReactSelect,
  GroupBase,
  MultiValueRemoveProps,
  Props as SelectProps,
} from 'react-select'

import { X } from '../icons'
import { classes, noop } from '../utils'
import FormInput from './FormInput'

export interface Props<
  T,
  IsMulti extends boolean,
  Group extends GroupBase<T> = GroupBase<T>,
> extends SelectProps<T, IsMulti, Group> {
  /** Label to show on the input. */
  label?: string
  /** HTML for attribute */
  htmlFor: string
  /** Externally defined className for the input wrapper. */
  wrapperClassName?: string
  /** Form field description. */
  description?: string
  /**
   * An error message to show when the field is invalid.
   */
  errorMessage?: string
  /** True if a value for the field is required */
  isRequired?: boolean
  /** True if the component is rendered as a textbox addon */
  isAddon?: boolean
}

/** Select box to be used within the scope of a form. */
const Select = <
  T,
  IsMulti extends boolean,
  Group extends GroupBase<T> = GroupBase<T>,
>({
  htmlFor,
  label,
  description,
  isRequired,
  wrapperClassName,
  options,
  className,
  errorMessage,
  ...other
}: Props<T, IsMulti, Group>) => {
  const MultiValueRemove = (
    props: MultiValueRemoveProps<T, IsMulti, Group>,
  ) => (
    <components.MultiValueRemove {...props}>
      <X className="h-3 w-3" data-testid="selectMultivalueRemove" />
    </components.MultiValueRemove>
  )

  const isInvalid = !!errorMessage

  return (
    <FormInput
      label={label}
      description={description}
      htmlFor={htmlFor}
      isRequired={isRequired}
      className={wrapperClassName}
      errorMessage={errorMessage}
    >
      <ReactSelect<T, IsMulti, Group>
        name={htmlFor}
        options={options}
        components={{ MultiValueRemove }}
        styles={{
          control: (provided) => ({
            height: 'full',
            width: 'full',
            display: provided.display,
          }),
          input: () => ({
            'input:focus': {
              boxShadow: 'none',
            },
          }),
          menu: (provided) => ({
            ...provided,
            ...commonResets,
            display: provided.display,
            zIndex: provided.zIndex,
            marginTop: undefined,
          }),
          option: () => ({}),
          placeholder: (provided) => ({
            ...provided,
            ...commonResets,
          }),
          singleValue: (provided) => ({
            ...provided,
            ...commonResets,
          }),
          multiValueLabel: (provided) => ({
            ...provided,
            whiteSpace: 'normal',
            overflow: 'visible',
            ...commonResets,
          }),
          multiValue: (provided) => ({
            ...provided,
            ...commonResets,
          }),
          multiValueRemove: () => ({}),
        }}
        className={className}
        // Note that an annoying amount of styles applied via `classNames` prop will conflict with default styles,
        // so we need to unset some above. I chose not to start from scratch with `unstyled` prop, though.
        // For more styling docs, see: https://react-select.com/styles#the-classnames-prop
        classNames={{
          container: () => 'bg-background-surface text-foreground-normal',
          control: ({ isFocused }) =>
            classes(
              'w-full rounded-md bg-background border border-border-normal text-base text-foreground-normal transition-colors duration-150 md:text-sm opacity-[0.85] enabled:hover:opacity-100 enabled:focus:opacity-100',
              { 'bg-background-surface': isFocused },
              { 'border-red-400': isInvalid },
            ),
          dropdownIndicator: () => 'text-foreground-normal',
          input: () => 'text-foreground-normal',
          indicatorSeparator: () => 'hidden',
          option: ({ isFocused, isSelected }) =>
            classes('text-foreground-normal p-1.5', {
              'bg-fill-brand-primary text-white': isFocused || isSelected,
            }),
          menu: () =>
            'bg-background-overlay border border-border-normal mt-0.5 rounded-lg',
          singleValue: ({ hasValue }) =>
            classes('text-foreground-subtle', {
              'text-foreground-normal': hasValue,
            }),
          menuList: () => 'flex flex-col space-y-1',
          multiValue: () => 'bg-fill-brand-primary text-white rounded-md',
          placeholder: () => 'text-foreground-subtle',
          multiValueRemove: (state) =>
            classes(
              'bg-transparent p-1 flex items-center justify-center hover:bg-background hover:bg-opacity-10',
              {
                'bg-background bg-opacity-10': state.isFocused,
              },
            ),
        }}
        {...other}
      />
    </FormInput>
  )
}

Select.defaultProps = {
  onChange: noop,
  isRequired: false,
  isSearchable: false,
}

export default Select

const commonResets = {
  background: undefined,
  backgroundColor: undefined,
  border: undefined,
  borderColor: undefined,
  borderRadius: undefined,
  color: undefined,
}
